import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

export interface DashboardNotification {
  id: string;
  type: 'deal' | 'order' | 'inventory' | 'wealth' | 'system';
  title: string;
  message: string;
  read: boolean;
  link?: string;
  created_at: string;
}

interface DashboardNotificationContextType {
  notifications: DashboardNotification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  addNotification: (notification: Omit<DashboardNotification, 'id' | 'read' | 'created_at'>) => void;
  loading: boolean;
}

const DashboardNotificationContext = createContext<DashboardNotificationContextType | undefined>(undefined);

export const DashboardNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<DashboardNotification[]>([]);
  const [loading, setLoading] = useState(true);

  // Mock initial notifications for a professional look
  const mockNotifications: DashboardNotification[] = [
    {
      id: '1',
      type: 'deal',
      title: 'New B2B Negotiation',
      message: 'Grand Nairobi Hotel sent a quote request for Organic Tomatoes.',
      read: false,
      link: '/dashboard/deals',
      created_at: new Date().toISOString()
    },
    {
      id: '2',
      type: 'inventory',
      title: 'Stock-out Risk Alert',
      message: 'AI predicts "Hybrid Maize Seeds" will be out of stock in 3 days.',
      read: false,
      link: '/dashboard/products',
      created_at: new Date(Date.now() - 3600000).toISOString()
    },
    {
      id: '3',
      type: 'wealth',
      title: 'Wealth Projection Updated',
      message: 'Your estimated annual revenue increased by KSh 50,000.',
      read: true,
      link: '/dashboard/wealth',
      created_at: new Date(Date.now() - 86400000).toISOString()
    }
  ];

  useEffect(() => {
    if (user) {
      setNotifications(mockNotifications);
      setLoading(false);
    } else {
      setNotifications([]);
      setLoading(false);
    }
  }, [user]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const addNotification = (notif: Omit<DashboardNotification, 'id' | 'read' | 'created_at'>) => {
    const newNotif: DashboardNotification = {
      ...notif,
      id: Math.random().toString(36).substr(2, 9),
      read: false,
      created_at: new Date().toISOString()
    };
    setNotifications(prev => [newNotif, ...prev]);
  };

  return (
    <DashboardNotificationContext.Provider value={{
      notifications,
      unreadCount,
      markAsRead,
      markAllAsRead,
      addNotification,
      loading
    }}>
      {children}
    </DashboardNotificationContext.Provider>
  );
};

export const useDashboardNotifications = () => {
  const context = useContext(DashboardNotificationContext);
  if (context === undefined) {
    throw new Error('useDashboardNotifications must be used within a DashboardNotificationProvider');
  }
  return context;
};
